import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ttsService } from '../services/tts';
import { useUserStore } from './user';

export const useTTSStore = create(
  persist(
    (set, get) => ({
      // State
      voices: [],
      selectedVoice: null,
      languages: [],
      selectedLanguage: 'en',
      textModels: [],
      selectedTextModel: '',
      history: [],
      historyTotal: 0,
      isLoadingVoices: false,
      isLoadingLanguages: false,
      isLoadingTextModels: false,
      isLoadingHistory: false,
      isConverting: false,
      isGeneratingText: false,
      currentAudio: null,
      generatedAudios: [], // Persist audio metadata (not blob URLs)
      error: null,

      // Actions
      fetchLanguages: async () => {
        set({ isLoadingLanguages: true });
        try {
          const data = await ttsService.getLanguages();
          set({ languages: data, isLoadingLanguages: false });
          return data;
        } catch (error) {
          console.error('Failed to fetch languages:', error);
          set({ isLoadingLanguages: false });
          return [];
        }
      },

      setLanguage: (languageCode) => set({ selectedLanguage: languageCode }),

      fetchTextModels: async () => {
        set({ isLoadingTextModels: true });
        try {
          const data = await ttsService.getTextModels();
          const models = data?.models || [];
          const currentSelectedModel = get().selectedTextModel;
          const selectedFromList = models.find((item) => item.model === currentSelectedModel);
          const fallbackModel = models[0]?.model || '';
          set({
            textModels: models,
            selectedTextModel: selectedFromList?.model || fallbackModel,
            isLoadingTextModels: false,
          });
          return models;
        } catch (error) {
          console.error('Failed to fetch text models:', error);
          set({ isLoadingTextModels: false });
          return [];
        }
      },

      setTextModel: (model) => set({ selectedTextModel: model }),

      generateText: async (prompt, options = {}) => {
        const selectedTextModel = options.model || get().selectedTextModel || undefined;
        set({ isGeneratingText: true, error: null });
        try {
          const result = await ttsService.generateText(prompt, {
            model: selectedTextModel,
            max_tokens: options.max_tokens,
            temperature: options.temperature,
          });
          set({ isGeneratingText: false });
          return { success: true, data: result };
        } catch (error) {
          console.error('Text generation error:', error);
          const message = error.response?.data?.detail || error.message || 'Text generation failed. Please try again.';
          set({ error: message, isGeneratingText: false });
          return { success: false, error: message };
        }
      },

      fetchVoices: async (language = null) => {
        set({ isLoadingVoices: true });
        try {
          const data = await ttsService.getVoices(language);
          const currentSelectedVoice = get().selectedVoice;
          const selectedFromList = currentSelectedVoice
            ? data.voices.find((voice) => voice.voice_id === currentSelectedVoice.voice_id)
            : null;

          set({
            voices: data.voices,
            isLoadingVoices: false,
            selectedVoice: selectedFromList || (data.voices[0] || null),
          });
          
          return data.voices;
        } catch (error) {
          console.error('Failed to fetch voices:', error);
          set({ isLoadingVoices: false });
          return [];
        }
      },

      selectVoice: (voice) => set({ selectedVoice: voice }),

      convert: async (text, options = {}) => {
        const { selectedVoice, selectedLanguage } = get();
        if (!selectedVoice) {
          return { success: false, error: 'No voice selected' };
        }

        const isClonedVoice = Boolean(
          selectedVoice.is_cloned || selectedVoice.voice_id?.startsWith('clone_')
        );
        const language = isClonedVoice
          ? 'en'
          : (options.language || selectedLanguage || 'en');

        set({ isConverting: true, error: null });
        try {
          const result = await ttsService.convert(text, selectedVoice.voice_id, { ...options, language });
          
          // Fetch audio as blob with authentication
          let audioUrl;
          try {
            audioUrl = await ttsService.getAudioBlob(result.id);
          } catch (audioError) {
            console.error('Failed to fetch audio blob:', audioError);
            // Fallback to direct URL
            audioUrl = ttsService.getAudioUrl(result.id);
          }
          
          // Get language name from languages list
          const languages = get().languages || [];
          const langInfo = languages.find(l => l.code === language);
          const languageName = langInfo?.name || language;
          
          const audioData = {
            id: result.id,
            text: text,
            voiceId: selectedVoice.voice_id,
            voiceName: selectedVoice.name,
            language: language,
            languageName: languageName,
            speed: options.speed || 1.0,
            audio_duration: result.audio_duration,
            processing_time: result.processing_time,
            cached: result.cached,
            createdAt: new Date().toISOString(),
          };
          
          set((state) => ({
            currentAudio: { ...audioData, url: audioUrl },
            // Add to generated audios list (keep last 50) - store metadata only
            generatedAudios: [audioData, ...(state.generatedAudios || []).filter(a => a.id !== result.id)].slice(0, 50),
            isConverting: false,
          }));
          
          // Refresh history to include the new audio
          get().fetchHistory();
          
          // Refresh user usage stats
          useUserStore.getState().fetchUsageStats();
          
          return { success: true, data: result };
        } catch (error) {
          console.error('Conversion error:', error);
          const message = error.response?.data?.detail || error.message || 'Conversion failed. Please try again.';
          set({ error: message, isConverting: false });
          return { success: false, error: message };
        }
      },

      // Apply speed to current audio (for playback rate)
      applySpeedToAudio: (speed) => {
        const { currentAudio } = get();
        if (currentAudio) {
          set({ 
            currentAudio: { ...currentAudio, playbackSpeed: speed }
          });
        }
      },

      // Load a previously generated audio (fetch fresh blob)
      loadGeneratedAudio: async (audio) => {
        try {
          // Fetch fresh blob URL from server
          const audioUrl = await ttsService.getAudioBlob(audio.id);
          set({ 
            currentAudio: { ...audio, url: audioUrl }
          });
          return { success: true };
        } catch (error) {
          console.error('Failed to load audio:', error);
          return { success: false, error: 'Failed to load audio' };
        }
      },

      // Delete a generated audio
      deleteGeneratedAudio: async (audioId) => {
        try {
          await ttsService.deleteRecord(audioId);
          set((state) => ({
            generatedAudios: (state.generatedAudios || []).filter(a => a.id !== audioId),
            history: state.history.filter(a => a.id !== audioId),
            historyTotal: state.historyTotal - 1,
            currentAudio: state.currentAudio?.id === audioId ? null : state.currentAudio,
          }));
          return { success: true };
        } catch (error) {
          console.error('Failed to delete audio:', error);
          return { success: false };
        }
      },

      fetchHistory: async (page = 1, perPage = 20) => {
        set({ isLoadingHistory: true });
        try {
          const data = await ttsService.getHistory(page, perPage);
          set({
            history: data.history,
            historyTotal: data.total,
            isLoadingHistory: false,
          });
          return data;
        } catch (error) {
          console.error('Failed to fetch history:', error);
          set({ isLoadingHistory: false });
          return null;
        }
      },

      deleteFromHistory: async (requestId) => {
        try {
          await ttsService.deleteRecord(requestId);
          set((state) => ({
            history: state.history.filter((item) => item.id !== requestId),
            historyTotal: state.historyTotal - 1,
            generatedAudios: (state.generatedAudios || []).filter(a => a.id !== requestId),
            currentAudio: state.currentAudio?.id === requestId ? null : state.currentAudio,
          }));
          return { success: true };
        } catch (error) {
          console.error('Failed to delete record:', error);
          return { success: false };
        }
      },

      clearCurrentAudio: () => set({ currentAudio: null }),
      clearError: () => set({ error: null }),
      
      // Clear all generated audios
      clearAllGeneratedAudios: () => set({ generatedAudios: [], currentAudio: null }),
    }),
    {
      name: 'voxifyai-tts-storage',
      partialize: (state) => ({ 
        // Only persist metadata, not blob URLs
        generatedAudios: state.generatedAudios,
        selectedVoice: state.selectedVoice,
        selectedLanguage: state.selectedLanguage,
        selectedTextModel: state.selectedTextModel,
      }),
    }
  )
);
